import { Injectable } from '@angular/core'
import { SignalStore } from '@awork/core/state/signal-store/signalStore'
import type { EnumKeyByValue } from '@awork/_shared/types/enum-key-by-value'
import type { Duration } from 'date-fns'

/**
 * Feature flag key definitions
 */
export enum FeatureFlag {
  connect = 'connect',
  mocoSelfService = 'moco-self-service',
  datadogRum = 'datadog-rum',
  datadogReplay = 'datadog-replay',
  datadogLogging = 'datadog-logging',
  loggingExperimentalFeatures = 'logging-experimental-features',
  specialEvent = 'special-event',
  customFields = 'custom-fields',
  customFieldSortingGrouping = 'custom-field-sorting-grouping',
  importers = 'importers',
  mondayImporter = 'monday-importer',
  importDocs = 'import-docs',
  asanaImporter = 'asana-importer',
  wmtOldPlanner = 'wmt-old-planner',
  wmtPlannerPerformance = 'wmt-planner-performance',
  fwkDocsOldPlanActivation = 'fwk-docs-old-plan-activation',
  fwkRealtimeConnectionWarning = 'fwk-realtime-connection-warning',
  fwkTagManagement = 'fwk-tag-management',
  fwkDocumentModal = 'fwk-document-modal',
  mergeTimesIntoCalendar = 'tt-merge-times-into-calendar',
  // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
  documentImport = 'import-docs',
  growthConnectOnboardingTimeout = 'growth-connect-onboarding-timeout',
  docsSpecialAccess = 'docs-special-access',
  growthOnboardingTasksDashboardWidgetDate = 'growth-onboarding-tasks-dashboard-widget-date',
  fwkGifInComments = 'fwk-gif-in-comments',
  archAIThreadReopening = 'arch-ai-thread-reopening',
  archAIAgentReportDashboards = 'arch-ai-agent-report-dashboards',
  archAIDefaultAgent = 'arch-ai-default-agent',
  archAIDashboardWidget = 'arch-ai-dashboard-widget',
  archAIThreadChangedItems = 'arch-ai-thread-changed-items',
  archAIWhatsApp = 'arch-ai-whatsapp',
  archAILocalFolders = 'arch-ai-local-folders',
  archAISkillPresets = 'arch-ai-skill-presets',
  archAIPromptLibrary = 'arch-ai-prompt-library',
  archAIMeetingRecordings = 'arch-ai-meeting-recordings',
  archAILibraryTagsAndOfficial = 'arch-ai-library-tags-and-official',
  aiAgentSchedulesSubDaily = 'ai-agent-schedules-sub-daily',

  aiThreadListGrouping = 'ai-thread-list-grouping',
  aiSlashCommands = 'ai-slash-commands',
  threadBatchOperations = 'thread-batch-operations',
  plannedEffortHierarchy = 'tt-planned-effort-hierarchy',
  wmtPehToggle = 'wmt-peh-toggle',
  archLiveEditing = 'arch-live-editing',
  jiraImporter = 'jira-importer',
  wmtExperimentUserCapacityLimits = 'wmt-experiment-user-capacity-limits',
  frameworkFriendlyFilters = 'framework-friendly-filters',
  frameworkFriendlyFiltersAi = 'framework-friendly-filters-ai',
  wmtAutomationsQ126 = 'wmt-automations-q1-26',
  productNpsToggle = 'product-nps-toggle',
  initializeBirdie = 'initialize-birdie',
  archProjectBatch = 'arch-project-batch',
  archApiCache = 'arch-api-cache',
  uiAnnotator = 'ui-annotator',
  agentsWaitlist = 'agents-waitlist',
  productNewDesignFeedbackIteration = 'product-new-design-feedback-iteration',
  timeReportEngine = 'tt-time-report-engine'
}

/**
 * Feature flag type definition
 */
export class FeatureFlagState implements FeatureFlagDefinition {
  connect: boolean
  mocoSelfService: boolean
  datadogRum: number
  datadogReplay: number
  datadogLogging: boolean
  loggingExperimentalFeatures: boolean
  specialEvent: { startDate: Date; endDate: Date }
  customFields: boolean
  customFieldSortingGrouping: boolean
  importers: boolean
  importDocs: boolean
  mondayImporter: boolean
  asanaImporter: boolean
  wmtOldPlanner: boolean
  wmtPlannerPerformance: boolean
  fwkDocsOldPlanActivation: boolean
  fwkRealtimeConnectionWarning: boolean
  fwkTagManagement: boolean
  fwkDocumentModal: boolean
  mergeTimesIntoCalendar: boolean
  fwkGifInComments: boolean
  documentImport: boolean
  growthConnectOnboardingTimeout: Duration
  docsSpecialAccess: boolean
  growthOnboardingTasksDashboardWidgetDate: string
  archAIThreadReopening: boolean
  archAIAgentReportDashboards: boolean
  archAIDefaultAgent: boolean
  archAIDashboardWidget: boolean
  archAIThreadChangedItems: boolean
  archAIWhatsApp: boolean
  archAILocalFolders: boolean
  archAISkillPresets: boolean
  archAIPromptLibrary: boolean
  archAIMeetingRecordings: boolean
  archAILibraryTagsAndOfficial: boolean
  aiAgentSchedulesSubDaily: boolean

  aiThreadListGrouping: boolean
  aiSlashCommands: boolean
  threadBatchOperations: boolean
  plannedEffortHierarchy: boolean
  wmtPehToggle: boolean
  archLiveEditing: boolean
  jiraImporter: boolean
  wmtExperimentUserCapacityLimits: boolean
  frameworkFriendlyFilters: boolean
  frameworkFriendlyFiltersAi: boolean
  wmtAutomationsQ126: boolean
  productNpsToggle: boolean
  archProjectBatch: boolean
  archApiCache: boolean
  initializeBirdie: boolean
  uiAnnotator: boolean
  agentsWaitlist: boolean
  productNewDesignFeedbackIteration: boolean
  timeReportEngine: boolean
}
export type LaunchDarklyFeatureFlags = Record<FeatureFlag, boolean | string | number | object>

export type FeatureFlagKey = keyof typeof FeatureFlag
export type FeatureFlagTypeByKey<K extends FeatureFlagKey> = FeatureFlagState[K]
export type FeatureFlagDefinition = { [key in FeatureFlagKey]: FeatureFlagTypeByKey<key> }

export type FeatureFlagType<K extends FeatureFlag> = FeatureFlagState[EnumKeyByValue<typeof FeatureFlag, K>]

/**
 * Starts each registered feature flag disabled.
 * @returns {FeatureFlagState} Initial feature flag values.
 */
function createInitialState(): FeatureFlagState {
  return Object.keys(FeatureFlag).reduce((acc, key) => {
    acc[key] = false
    return acc
  }, {} as FeatureFlagState)
}

@Injectable({ providedIn: 'root' })
export class FeatureFlagStore extends SignalStore<FeatureFlagState> {
  constructor() {
    super({ name: 'featureFlag', initialState: createInitialState() })
  }

  /**
   * Updates the feature flags
   * @param {LaunchDarklyFeatureFlags} flags
   */
  updateFlags(flags: LaunchDarklyFeatureFlags): void {
    const normalizedFlags = this.mapFeatureFlags(flags)
    this.update(normalizedFlags)
  }

  /**
   * Maps the LaunchDarkly feature flags to the FeatureFlagState.
   * If a flag is not found in the FeatureFlag enum, it is ignored.
   * @param {LaunchDarklyFeatureFlags} flags
   * @returns {FeatureFlagState}
   */
  private mapFeatureFlags(flags: LaunchDarklyFeatureFlags): FeatureFlagState {
    return Object.keys(flags).reduce((acc, key) => {
      const enumKey = Object.keys(FeatureFlag)[Object.values(FeatureFlag).indexOf(key as FeatureFlag)]

      if (enumKey !== undefined) {
        acc[enumKey] = flags[key]
      }

      return acc
    }, {} as FeatureFlagState)
  }
}
